import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Check, Trash2, Clock } from "lucide-react";
import { API, api } from "@/lib/api";

export const fmtT = (s) => { const t = Math.max(0, Math.floor(s || 0)); return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`; };
export const streamUrl = (token, n) => `${API}/public/videos/${token}/stream${n ? `?v=${n}` : ""}`;
const KIND = { agency: "bg-[#EDE7FF] text-[#5b2fd6]", client: "bg-[#E6F7EE] text-[#1f9d55]", guest: "bg-[#FFF4E0] text-[#b06a00]" };

export async function uploadVideoFile(videoId, file, onProgress, client = api) {
  const { data: init } = await client.post(`/console/videos/${videoId}/upload/init`, { filename: file.name, mime: file.type || "video/mp4", size: file.size });
  const size = init.chunk_size;
  for (let i = 0, off = 0; off < file.size; i += 1, off += size) {
    let tries = 0;
    for (;;) {
      try { await client.post(`/video-uploads/${init.upload_id}/chunk?index=${i}`, file.slice(off, off + size), { headers: { "Content-Type": "application/octet-stream" } }); break; }
      catch (e) { tries += 1; if (tries >= 3) throw e; await new Promise((r) => setTimeout(r, 1000 * tries)); }
    }
    onProgress?.(Math.min(100, Math.round(((off + size) / file.size) * 100)));
  }
  const duration = await new Promise((res) => { const v = document.createElement("video"); v.preload = "metadata"; const u = URL.createObjectURL(file); const done = (d) => { URL.revokeObjectURL(u); res(Number.isFinite(d) ? d : 0); }; v.onloadedmetadata = () => done(v.duration); v.onerror = () => done(0); v.src = u; });
  const { data } = await client.post(`/video-uploads/${init.upload_id}/complete`, { duration });
  return data;
}

export function VideoReviewer({ src, comments, version, canComment = true, onAddComment, onResolve, onDelete, extraComposer, onProgress }) {
  const ref = useRef(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [dur, setDur] = useState(0);
  const list = (comments || []).filter((c) => c.version === version);
  const seek = (t) => { if (ref.current) { ref.current.currentTime = t; ref.current.play().catch(() => {}); } };
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await onAddComment(text.trim(), ref.current?.currentTime || 0); setText(""); } catch { /* caller shows the error */ } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-5" data-testid="video-reviewer">
      <div>
        <div className="rounded-2xl overflow-hidden bg-black aspect-video">
          <video ref={ref} key={src} src={src} controls playsInline preload="metadata" onLoadedMetadata={(e) => setDur(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)} onDurationChange={(e) => Number.isFinite(e.currentTarget.duration) && setDur(e.currentTarget.duration)} onTimeUpdate={(e) => onProgress?.(e.currentTarget.currentTime)} onPause={(e) => onProgress?.(e.currentTarget.currentTime, true)} onEnded={(e) => onProgress?.(e.currentTarget.duration, true)} className="w-full h-full" data-testid="video-player" />
        </div>
        {dur > 0 && (
          <div className="relative h-3 mt-2 rounded-full bg-[#F0F0F5]" data-testid="video-comment-markers">
            {list.map((c) => <button key={c.id} title={`${fmtT(c.t)} ${c.author_name}`} onClick={() => seek(c.t)} style={{ left: `${Math.min(99, (c.t / dur) * 100)}%` }} className={`absolute top-0 h-3 w-1.5 -ml-0.5 rounded-full ${c.resolved ? "bg-[#c4c5cc]" : "bg-[#723FFF]"}`} />)}
          </div>
        )}
        {canComment && (
          <div className="mt-4 rounded-2xl border border-[#EEEFEF] bg-white p-3" data-testid="video-comment-composer">
            {extraComposer}
            <Textarea data-testid="video-comment-text" value={text} onChange={(e) => setText(e.target.value)} onFocus={() => ref.current?.pause()} rows={2} placeholder="Pause where you want to comment, then type here…" className="border-[#EEEFEF] bg-[#F6F6FD]" />
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px] text-[#9FA1A4] flex items-center gap-1"><Clock className="h-3 w-3" /> Comment is pinned to the current time</span>
              <Button data-testid="video-comment-submit" size="sm" onClick={submit} disabled={busy || !text.trim()} className="rounded-full bg-[#06060C] text-white hover:bg-[#1c1c24]">Comment</Button>
            </div>
          </div>
        )}
      </div>
      <div className="rounded-2xl border border-[#EEEFEF] bg-white flex flex-col max-h-[70vh]" data-testid="video-comments">
        <p className="px-4 py-3 border-b border-[#f0f0f3] text-xs font-mono-data uppercase tracking-wider text-[#9FA1A4] flex items-center gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> {list.length} comment{list.length !== 1 ? "s" : ""} · v{version}</p>
        <div className="overflow-y-auto divide-y divide-[#f6f6f8]">
          {list.length === 0 && <p className="p-4 text-sm text-[#9FA1A4]">No comments on this version yet.</p>}
          {list.map((c) => (
            <div key={c.id} data-testid={`video-comment-${c.id}`} className={`p-3.5 ${c.resolved ? "opacity-50" : ""}`}>
              <div className="flex items-center gap-2">
                <button data-testid={`video-comment-seek-${c.id}`} onClick={() => seek(c.t)} className="font-mono-data text-[11px] rounded-full bg-[#06060C] text-white px-2 py-0.5 hover:bg-[#723FFF]">{fmtT(c.t)}</button>
                <span className="text-sm font-medium text-[#06060C] truncate">{c.author_name}</span>
                <span className={`text-[9px] uppercase tracking-wider rounded-full px-1.5 py-0.5 ${KIND[c.author_kind] || ""}`}>{c.author_kind}</span>
                <span className="ml-auto flex gap-1.5">
                  {onResolve && <button data-testid={`video-comment-resolve-${c.id}`} onClick={() => onResolve(c)} title={c.resolved ? "Reopen" : "Resolve"} className="text-[#9FA1A4] hover:text-[#1f9d55]"><Check className="h-3.5 w-3.5" /></button>}
                  {onDelete && <button onClick={() => onDelete(c)} title="Delete" className="text-[#c4c5cc] hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                </span>
              </div>
              <p className="text-sm text-[#595D62] mt-1.5 whitespace-pre-wrap">{c.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const VIDEO_STATUS = { draft: ["Draft", "bg-[#F6F6FD] text-[#595D62]"], internal_review: ["Internal review", "bg-[#FFF4E0] text-[#b06a00]"], with_client: ["With client", "bg-[#EDE7FF] text-[#5b2fd6]"], changes_requested: ["Changes requested", "bg-rose-50 text-rose-600"], approved: ["Approved", "bg-[#E6F7EE] text-[#1f9d55]"] };
export function VideoStatusPill({ status }) {
  const [l, c] = VIDEO_STATUS[status] || VIDEO_STATUS.draft;
  return <span data-testid="video-status" className={`text-[10px] font-mono-data uppercase tracking-wider rounded-full px-2 py-0.5 ${c}`}>{l}</span>;
}
